import type { DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { observer } from 'mobx-react-lite'
import {
  useProvideWizardBottomBarPrimaryButton,
  WizardPageAnimatedContent,
} from '@/components/Wizard'
import {
  CheckoutView,
  CheckoutViewFooter,
  CheckoutViewMain,
  CheckoutViewMainContent,
} from '@/features/jobs/drafting/checkout/components/CheckoutView/index'
import { useMemo } from 'react'
import { NewBillingMethodDialog } from '@/features/billing/profile/NewBillingMethodDialog'
import { ButtonGroup } from '@/components/ButtonGroup'
import { Button, type ButtonProps } from '@/components/Button'
import {
  CheckoutBillingSectionContainer,
  CheckoutViewCouponSectionContainer,
} from '@/features/jobs/drafting/checkout/components/CheckoutView/CheckoutBillingSectionContainer'
import { CheckoutSummaryContainer } from '@/features/jobs/drafting/checkout/components/CheckoutView/CheckoutSummaryContainer'
import { DraftJobWizardCheckoutSkeleton } from '@/features/jobs/drafting/components/DraftJobWizardContentSkeleton'
import { CheckoutViewHeaderContainer } from '@/features/jobs/drafting/checkout/components/CheckoutView/CheckoutViewHeaderContainer'
import { formatMoney } from '@/utils/money-util'
import { useRootStore } from '@/contexts/root-store-context'

interface CheckoutViewContainerProps {
  vm: DraftJobViewModel
  formId: string
}

/**
 * Checkout View
 */
export const CheckoutViewContainer = observer(function CheckoutViewContainer({
  vm,
  formId,
}: CheckoutViewContainerProps) {
  const isViewSkeleton = vm.prepare.pending
  const {
    shell: { colorScheme$ },
  } = useRootStore()

  return (
    <>
      <WizardPageAnimatedContent
        animationKey="checkout"
        animationDirection="forward"
      >
        {isViewSkeleton ? (
          <DraftJobWizardCheckoutSkeleton
            isBillingEnabled={vm.checkoutVm.billingVm.isBillingEnabled}
            isSummaryVisible={vm.checkoutVm.isQuoteEnabled}
          />
        ) : (
          <CheckoutView
            testId="checkout-view"
            summary={<CheckoutSummaryContainer vm={vm} />}
            footer={
              <>
                <CheckoutViewCouponSectionContainer vm={vm} />
                <CheckoutViewFooterContainer vm={vm} formId={formId} />
              </>
            }
          >
            <CheckoutViewMain>
              <CheckoutViewHeaderContainer vm={vm} />
              <CheckoutViewMainContent>
                <CheckoutBillingSectionContainer
                  formId={formId}
                  vm={vm}
                  colorScheme$={colorScheme$}
                />
              </CheckoutViewMainContent>
            </CheckoutViewMain>
          </CheckoutView>
        )}
      </WizardPageAnimatedContent>
      <NewBillingMethodDialog
        vm={vm.checkoutVm.billingVm.newBillingMethodDialogVm}
      />
    </>
  )
})

interface CheckoutViewFooterContainerProps {
  vm: DraftJobViewModel
  formId?: string | null
}

/**
 * Checkout View Footer Container
 */
const CheckoutViewFooterContainer = observer(
  function CheckoutViewFooterContainer({
    vm,
    formId,
  }: CheckoutViewFooterContainerProps) {
    const isSkeleton = vm.checkoutVm.isFetchingInitial
    const isDisabled = !vm.checkoutVm.isSubmitEnabled
    const isPending = vm.checkoutVm.submit.pending
    const onSubmit = vm.checkoutVm.submit
    // Only add the `form` attribute if we're showing the card method editor.
    formId = vm.checkoutVm.billingVm.cardBillingMethodEditorVm ? formId : null

    // If the user is to be charged immediately, the button will include the
    // amount.
    const label = vm.checkoutVm.quote?.price?.upfrontAmount
      ? `Pay ${formatMoney(vm.checkoutVm.quote.price.upfrontAmount)}`
      : 'Confirm'

    const buttonProps: ButtonProps = useMemo(
      (): ButtonProps => ({
        label,
        isSkeleton,
        isDisabled,
        type: formId ? 'submit' : 'button',
        form: formId ?? undefined,
        isPending,
        onPress: formId ? undefined : onSubmit,
        testId: 'submit-job-button',
      }),
      [label, isSkeleton, isDisabled, formId, isPending, onSubmit],
    )
    useProvideWizardBottomBarPrimaryButton(buttonProps)

    return (
      <CheckoutViewFooter>
        <ButtonGroup stretch stacked>
          <Button {...buttonProps} />
        </ButtonGroup>
      </CheckoutViewFooter>
    )
  },
)
